// HealthBar — barra de saúde da operação no topo do dashboard.
// Mostra KPIs glanceable + chips clicáveis + banner de atenção (se houver).

import { useMemo } from 'react';
import { osLabelByFields } from '../lib/visita.js';
import VoalleSyncBadge from './VoalleSyncBadge.jsx';

export default function HealthBar({ resumo, atencao = [], ts, meta, onFiltroChip, filtroAtivo, mostrarBanner = true }) {
  if (!resumo) {
    return <div className="card" style={{ padding: 16 }}>Carregando resumo...</div>;
  }

  const {
    total, concluidas, em_andamento,
    em_andamento_no_horario = 0,
    em_andamento_atrasada = 0,
    proxima_2h, atrasadas, agendadas, sem_tecnico, sla_pct
  } = resumo;
  const slaCor = sla_pct >= 90 ? '#059669' : sla_pct >= 75 ? '#d97706' : '#dc2626';

  // Resumo do banner (texto único)
  const atencaoTexto = useMemo(() => {
    if (atencao.length === 0) return null;
    const exemplos = atencao.slice(0, 3).map(a => {
      if (a.tipo === 'atrasada') return `OS ${osLabelByFields(a.voalle_protocolo, a.visita_id)} (${a.tecnico_apelido || 'sem técnico'}) há ${a.minutos_atraso}min`;
      if (a.tipo === 'sem_tecnico') return `OS ${osLabelByFields(a.voalle_protocolo, a.visita_id)} sem técnico`;
      if (a.tipo === 'tecnico_parado') return `${a.tecnico_apelido} parado próximo a visita`;
      return a.motivo;
    });
    return exemplos.join(' · ');
  }, [atencao]);

  const tempoAtualizacao = ts
    ? `${Math.max(1, Math.floor((Date.now() - new Date(ts).getTime()) / 1000))}s atrás`
    : '—';

  function clickChip(filtro) {
    if (onFiltroChip) onFiltroChip(filtroAtivo === filtro ? null : filtro);
  }

  return (
    <div className="health-bar">
      <div className="health-top">
        <div className="health-title">
          <h1>Operação ao vivo</h1>
          <span className="sla">
            SLA hoje: <strong style={{ color: slaCor }}>{sla_pct}% no prazo</strong>
            <span style={{ color: '#9ca3af' }}> · {total} OS</span>
          </span>
        </div>
        <div className="auto-refresh">
          <span className="dot"></span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3, gap: 2 }}>
            <span>Tela atualizada há {tempoAtualizacao} · refresh 30s</span>
            {meta?.lag_min != null && (
              <span style={{ fontSize: 11, color: meta.lag_min > 15 ? '#dc2626' : meta.lag_min > 10 ? '#d97706' : '#6b7280' }}>
                Datalake Voalle: dados de {meta.lag_min} min atrás
              </span>
            )}
            {/* Sync API Voalle (movido pra cá — antes ficava solto no rodapé) */}
            <VoalleSyncBadge />
          </div>
        </div>
      </div>

      {/* Barra de progresso por status */}
      {total > 0 && (
        <div className="progress-bar">
          <div className="progress-fill">
            {concluidas > 0    && <div style={{ background: '#22c55e', flex: concluidas }} title={`${concluidas} concluídas`}/>}
            {em_andamento > 0  && <div style={{ background: '#3b82f6', flex: em_andamento }} title={`${em_andamento} em andamento`}/>}
            {proxima_2h > 0    && <div style={{ background: '#fbbf24', flex: proxima_2h }} title={`${proxima_2h} próximas 2h`}/>}
            {agendadas > 0     && <div style={{ background: '#cbd5e1', flex: agendadas }} title={`${agendadas} agendadas`}/>}
            {atrasadas > 0     && <div style={{ background: '#ef4444', flex: atrasadas }} title={`${atrasadas} atrasadas`}/>}
          </div>
        </div>
      )}

      {/* 6 chips em ordem: cinza, amarelo, azul, laranja, verde, vermelho — sempre visíveis */}
      <div className="status-chips">
        <button className={`chip neutral ${filtroAtivo==='agendada'?'active':''}`} onClick={() => clickChip('agendada')}>
          <span className="dot"></span>
          <span className="count">{agendadas}</span>
          <span>agendadas</span>
        </button>
        <button className={`chip pending ${filtroAtivo==='proxima_2h'?'active':''}`} onClick={() => clickChip('proxima_2h')}>
          <span className="dot"></span>
          <span className="count">{proxima_2h}</span>
          <span>próximas 2h</span>
        </button>
        <button className={`chip running ${filtroAtivo==='em_andamento_no_horario'?'active':''}`} onClick={() => clickChip('em_andamento_no_horario')}>
          <span className="dot"></span>
          <span className="count">{em_andamento_no_horario}</span>
          <span>em andamento</span>
        </button>
        <button className={`chip warn ${filtroAtivo==='em_andamento_atrasada'?'active':''}`} onClick={() => clickChip('em_andamento_atrasada')}>
          <span className="dot"></span>
          <span className="count">{em_andamento_atrasada}</span>
          <span>em and. atrasada</span>
        </button>
        <button className={`chip done ${filtroAtivo==='concluida'?'active':''}`} onClick={() => clickChip('concluida')}>
          <span className="dot"></span>
          <span className="count">{concluidas}</span>
          <span>concluídas</span>
        </button>
        <button className={`chip late ${filtroAtivo==='atrasada'?'active':''}`} onClick={() => clickChip('atrasada')}>
          <span className="dot"></span>
          <span className="count">{atrasadas}</span>
          <span>atrasadas</span>
        </button>
      </div>

      {mostrarBanner && atencaoTexto && (
        <div className="attention-banner">
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span>
            <strong>{atencao.length} {atencao.length === 1 ? 'OS precisa' : 'OS precisam'} atenção</strong>
            {' · '}{atencaoTexto}
          </span>
        </div>
      )}
    </div>
  );
}
